import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink } from 'lucide-react';
import './DocumentPreview.css';

interface DocumentPreviewProps {
  fileName: string;
  fileType: string;
  downloadUrl: string;
  caseId?: string;
  docId?: string;
  onClose: () => void;
}

// Component for displaying text files with bright, readable styling
const TextFilePreview: React.FC<{ downloadUrl: string }> = ({ downloadUrl }) => {
  const [textContent, setTextContent] = useState<string>('Loading...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTextContent = async () => {
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to load text file');
        }
        const text = await response.text();
        setTextContent(text);
      } catch (err) {
        setError('Failed to load text file. Please try downloading it instead.');
        console.error('Error loading text file:', err);
      }
    };

    fetchTextContent();
  }, [downloadUrl]);

  if (error) {
    return (
      <div className="preview-text-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="preview-text-container">
      <pre className="preview-text-content">{textContent}</pre>
    </div>
  );
};

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  fileName,
  downloadUrl,
  caseId,
  docId,
  onClose,
}) => {

  const getFileExtension = () => {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  };

  const extension = getFileExtension();
  
  // Construct preview URL for document conversion endpoint
  const getPreviewContentUrl = () => {
    if (!caseId || !docId) return null;
    
    // Get the API base URL from environment or construct it
    const apiUrl = import.meta.env.VITE_API_URL || 'https://1v6zm22cmf.execute-api.us-west-2.amazonaws.com/prod/';
    const baseUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash if present
    
    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    
    // Construct the preview-content endpoint URL
    return `${baseUrl}/cases/${caseId}/documents/${encodeURIComponent(docId)}/preview-content${token ? `?token=${token}` : ''}`;
  };

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const renderPreview = () => {
    // PDF
    if (extension === 'pdf') {
      return (
        <iframe
          src={downloadUrl}
          className="preview-iframe"
          title={fileName}
        />
      );
    }

    // DOCX and EML - use preview-content endpoint
    if (['docx', 'eml'].includes(extension)) {
      const previewUrl = getPreviewContentUrl();
      if (previewUrl) {
        return (
          <iframe
            src={previewUrl}
            className="preview-iframe"
            title={fileName}
          />
        );
      }
    }

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
      return (
        <div className="preview-image-container">
          <img src={downloadUrl} alt={fileName} className="preview-image" />
        </div>
      );
    }

    // Audio
    if (['mp3', 'wav', 'm4a', 'ogg'].includes(extension)) {
      // Map file extensions to proper MIME types
      const audioMimeTypes: { [key: string]: string } = {
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'm4a': 'audio/mp4',
        'ogg': 'audio/ogg',
      };
      const mimeType = audioMimeTypes[extension] || `audio/${extension}`;
      
      return (
        <div className="preview-audio-container">
          <audio controls className="preview-audio">
            <source src={downloadUrl} type={mimeType} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    // Video
    if (['mp4', 'webm', 'ogg'].includes(extension)) {
      return (
        <div className="preview-video-container">
          <video controls className="preview-video">
            <source src={downloadUrl} type={`video/${extension}`} />
            Your browser does not support the video element.
          </video>
        </div>
      );
    }

    // Text files - fetch and display with custom styling for readability
    if (['txt', 'md', 'log'].includes(extension)) {
      return <TextFilePreview downloadUrl={downloadUrl} />;
    }

    // Cannot preview - show download option
    return (
      <div className="preview-no-preview">
        <div className="no-preview-icon">
          <ExternalLink size={48} />
        </div>
        <p className="no-preview-message">
          Preview not available for {extension.toUpperCase()} files
        </p>
        <p className="no-preview-submessage">
          Click the download button below to view this file
        </p>
      </div>
    );
  };

  return (
    <>
      <div className="document-preview-backdrop" onClick={onClose} />
      <div className="document-preview-modal">
        <div className="preview-header">
          <div className="preview-title">
            <span className="preview-file-name">{fileName}</span>
            <span className="preview-file-type">{extension.toUpperCase()}</span>
          </div>
          <div className="preview-actions">
            <button onClick={handleDownload} className="preview-action-btn" title="Download">
              <Download size={20} />
            </button>
            <button onClick={onClose} className="preview-action-btn" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="preview-content">
          {renderPreview()}
        </div>
      </div>
    </>
  );
};

export default DocumentPreview;

